import { Component, computed, inject } from '@angular/core';
import { Att, AttrPath, Table } from '@cumlaude/metadata';
import { initial, last, range } from 'lodash-es';
import { Observable } from 'rxjs';
import { DashboardHeaderComponent } from '../../dashboard-header/dashboard-header.component';
import { FilterBarComponent } from '../../filter-bar/filter-bar.component';
import { FilterPanelComponent } from '../../filter-panel/filter-panel.component';
import { LegendaComponent } from '../../legenda/legenda.component';
import { DashboardContainerComponent } from '../../layout/dashboard-container/dashboard-container.component';
import { noAgg0 } from '../../services/aggregation';
import { getLeafA, Level, Path } from '../../services/data-tree';
import { AanwezigheidMeasure, BasicFilterExpression, DataOptions, DataResponse, DataService } from '../../services/data.service';
import { FilterName } from '../../services/filter-name';
import { att, percOfRow } from '../../services/measures';
import { BarInfo } from '../../services/stacked-bars';
import { Sort } from '../../shared/components/table/table/table.component';
import { ColumnDef, TableModel } from '../../shared/components/table/table/table.model';
import { BarchartTableConfig } from '../../shared/dashboard/barchart-table/barchart-table-config';
import { BarchartTableComponent } from '../../shared/dashboard/barchart-table/barchart-table.component';
import { Attributes, BaseDashboardConfig, SelectionConfig } from '../../shared/dashboard/base-dashboard/base-dashboard-config';
import { DashboardContext, DataRequestContext } from '../../shared/dashboard/base-dashboard/dashboard-context';
import { createMeasureColumn, DataRow } from '../../shared/dashboard/data-tree-table/data-tree-table';
import { DataTreeTableConfig } from '../../shared/dashboard/data-tree-table/data-tree-table-config';
import { UrlTree } from '@angular/router';
import { getAfgeslotenMaandenFilter } from '../../services/schoolaanwezigheid';

interface AanwezigheidDatavolledigheidI extends Attributes {
	aw_m_nr_lessen: number;
	aw_m_nr_lessen_presentieregistratie_verwerkt: number;
}

interface AanwezigheidDatavolledigheidA extends Attributes {
	afgesloten: boolean;
	aw_m_nr_lessen: number;
	aw_m_nr_lessen_presentieregistratie_verwerkt: number;
}

/**
 * Groepeert en filtert op de eigenschappen van de les (de _afspraak-velden) en niet op die van de
 * leerling. Bij twee lessen op hetzelfde lesuur is aan de leerlingkant niet te zien welke docent bij
 * welke les hoorde en welke les wel of niet is afgesloten; dat is precies wat dit dashboard nodig
 * heeft.
 */
@Component({
	selector: 'app-aanwezigheid-datavolledigheid',
	templateUrl: './aanwezigheid-datavolledigheid.component.html',
	styleUrls: ['./aanwezigheid-datavolledigheid.component.scss'],
	imports: [
		DashboardContainerComponent,
		FilterPanelComponent,
		DashboardHeaderComponent,
		BarchartTableComponent,
		FilterBarComponent,
		LegendaComponent,
	],
})
export class AanwezigheidDatavolledigheidComponent extends BarchartTableConfig<AanwezigheidDatavolledigheidI, AanwezigheidDatavolledigheidA> {
	private readonly dataService = inject(DataService);
	readonly factTable = Table.fac_aw_aanwezigheid;

	readonly defaultFilters: FilterName[] = [
		'aw_nm_schooljaar',
		'aw_fk_lb.lb_co_brin',
		'aw_fk_vs.vs_nm_vestiging',
		'aw_fk_ilt.ilt_nm_niveau',
		'aw_nr_leerjaar',
		'aw_nm_klas',
		'aw_fun_nm_medewerker_afspraak',
		'aw_fun_nm_vak_afspraak',
		'aw_d_datum.per_d_datum',
		'aw_nr_lesuur',
		'aw_fun_nm_lesgroep_afspraak',
		'aw_fun_is_presentieregistratie_verplicht_afspraak',
	];

	/**
	 * Standaard alleen lessen waarvoor presentieregistratie verplicht is.
	 * Bij Magister bestaat het filter niet (blocklist), dus daar is dit zonder effect.
	 */
	readonly overrideDefault: Partial<{ [name in FilterName]: any }> = { aw_fun_is_presentieregistratie_verplicht_afspraak: 1 };

	override availableGroups: AttrPath[] = [
		['aw_fk_vs', 'vs_nm_vestiging'],
		['aw_fk_ilt', 'ilt_nm_niveau'],
		['aw_nr_leerjaar'],
		['aw_nm_klas'],
		['aw_fun_nm_medewerker_afspraak'],
		['aw_fun_nm_vak_afspraak'],
		['aw_d_datum', 'per_d_datum'],
		['aw_nr_lesuur'],
		['aw_fun_nm_lesgroep_afspraak'],
	];

	// Alleen afgesloten maanden (+huidige) en alleen vestigingen die Schoolaanwezigheid hebben afgenomen.
	// Dit zijn dezelfde filters als in het dashboard Aanwezigheidsoverzicht (m.u.v. afgesloten lessen), zodat de data in beide dashboards consistent is.
	readonly permanentFilterExpressions = computed(() => [
		getAfgeslotenMaandenFilter(),
		new BasicFilterExpression(['aw_id_vestiging'], this.userService.schoolaanwezigheidVestigingen(), 'in'),
	]);

	override getData(options: DataOptions): Observable<DataResponse<number[]>> {
		return this.dataService.getAanwezigheidData({
			...options,
			m: [AanwezigheidMeasure.LESSEN],
			r: range(0, this.groeperingService.huidigeAantalGroeperingen() + 1),
		});
	}

	protected override singleAggregators = {
		aw_m_nr_lessen: noAgg0<'aw_m_nr_lessen', AanwezigheidDatavolledigheidI>('aw_m_nr_lessen'),
		aw_m_nr_lessen_presentieregistratie_verwerkt: noAgg0<'aw_m_nr_lessen_presentieregistratie_verwerkt', AanwezigheidDatavolledigheidI>(
			'aw_m_nr_lessen_presentieregistratie_verwerkt'
		),
	};

	override createMeasureColumns(
		_context: DashboardContext<
			AanwezigheidDatavolledigheidI,
			AanwezigheidDatavolledigheidA,
			DataTreeTableConfig<AanwezigheidDatavolledigheidI, AanwezigheidDatavolledigheidA>
		>
	): ColumnDef<DataRow<AanwezigheidDatavolledigheidA>>[] {
		return [
			createMeasureColumn('Aantal lessen', att('aw_m_nr_lessen')),
			createMeasureColumn('Afgesloten', att('aw_m_nr_lessen_presentieregistratie_verwerkt')),
			createMeasureColumn(
				'Niet-afgesloten',
				(row) => getLeafA(row).aw_m_nr_lessen - getLeafA(row).aw_m_nr_lessen_presentieregistratie_verwerkt
			),
			createMeasureColumn('Percentage', percOfRow('aw_m_nr_lessen_presentieregistratie_verwerkt', 'aw_m_nr_lessen'), {
				dataType: 'percentage',
			}),
		];
	}

	override getDefaultSort(_tableModel: TableModel<DataRow<AanwezigheidDatavolledigheidA>>): Sort {
		return { active: 'Percentage', direction: 'asc' };
	}

	override partitionBarData(
		rowRoot: Level<AanwezigheidDatavolledigheidA, number[]>,
		_context: DashboardContext<
			AanwezigheidDatavolledigheidI,
			AanwezigheidDatavolledigheidA,
			BaseDashboardConfig<AanwezigheidDatavolledigheidI, AanwezigheidDatavolledigheidA>
		>
	): Path<AanwezigheidDatavolledigheidA, number[]>[][] {
		const path = rowRoot.r[0];
		if (!path) return [];
		const a = getLeafA(path);
		// Verdeel de data in twee bars: één voor afgesloten lessen en één voor niet-afgesloten lessen.
		// Hiervoor wordt de path van de rij gedupliceerd en wordt er een extra attribuut 'afgesloten' toegevoegd om aan te geven
		// of het gaat om de bar voor afgesloten of niet-afgesloten lessen.
		return [[true, false].map((afgesloten) => [...initial(path), { ...last(path)!, a: { ...a, afgesloten } }])];
	}

	override makeBar(
		attrs: AanwezigheidDatavolledigheidI,
		path: Path<AanwezigheidDatavolledigheidA, number[]>,
		context: DashboardContext<
			AanwezigheidDatavolledigheidI,
			AanwezigheidDatavolledigheidA,
			BaseDashboardConfig<AanwezigheidDatavolledigheidI, AanwezigheidDatavolledigheidA>
		>
	): BarInfo {
		const { aw_m_nr_lessen, aw_m_nr_lessen_presentieregistratie_verwerkt } = attrs;
		// Bepaal of deze bar gaat over afgesloten of niet-afgesloten lessen aan de hand van het 'afgesloten' attribuut dat is toegevoegd in partitionBarData.
		const afgesloten = getLeafA(path).afgesloten;
		const size = afgesloten ? aw_m_nr_lessen_presentieregistratie_verwerkt : aw_m_nr_lessen - aw_m_nr_lessen_presentieregistratie_verwerkt;
		const percentage = aw_m_nr_lessen > 0 ? Math.round((size / aw_m_nr_lessen) * 100) : 0;
		return {
			...super.makeBar(attrs, path, context),
			size,
			selection: undefined, // geen doorklik op de bar, alleen de hele row via getRowClickUrl
			className: afgesloten ? 'afgesloten' : 'niet-afgesloten',
			tooltip: `${size} van ${aw_m_nr_lessen} lessen ${afgesloten ? 'afgesloten' : 'niet-afgesloten'} (${percentage}%)`,
		};
	}

	override getRowClickUrl(
		context: DashboardContext<
			AanwezigheidDatavolledigheidI,
			AanwezigheidDatavolledigheidA,
			DataTreeTableConfig<AanwezigheidDatavolledigheidI, AanwezigheidDatavolledigheidA>
		>,
		path: Path<AanwezigheidDatavolledigheidA, number[]>
	): UrlTree | undefined {
		// Moet overeenkomen met availableGroups, anders herkent getZoomClickUrl de groepering niet
		const atts: Att[] = [
			'aw_fun_nm_vak_afspraak',
			'aw_fun_nm_medewerker_afspraak',
			'aw_d_datum.per_d_datum',
			'aw_nr_lesuur',
			'aw_fk_ll.ll_nm_leerling',
		];
		return this.getZoomClickUrl(atts, context.groupNames, path) ?? super.getRowClickUrl(context, path);
	}

	override getSelectionConfig(
		_context: DataRequestContext<AanwezigheidDatavolledigheidA>
	): SelectionConfig<AanwezigheidDatavolledigheidA> | undefined {
		return undefined;
	}
}
